import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import Paginator from './Paginator';
import { buildAdapterColumns, renderCell, getCellValue } from './columnUtils';

// TableAdapter.jsx
// Drop-in-ish replacement for react-bootstrap-table-next, backed by
// @tanstack/react-table concepts (row model) but with a bespoke render so we
// can keep the exact legacy column API used across the app:
//   - dataField / text / formatter(cell, row) / isDummyField / hidden
//   - headerStyle / align / headerAlign
//   - expandRow: { renderer, showExpandColumn, expandByColumnOnly }
//   - remote pagination via { pageIndex, pageSize, totalSize } + onTableChange
//
// This intentionally does NOT use useReactTable's own pagination/row-model
// machinery for remote mode: the app already owns page state in Redux, so
// TableAdapter just renders rows as given and delegates paging UI to
// <Paginator />, keeping onTableChange('pagination', { page, sizePerPage })
// wire-compatible with existing handlers (e.g. LabelList.handleTableChange).

const TableAdapter = ({
    keyField,
    data = [],
    columns = [],
    expandRow,
    remote,
    pagination,
    onTableChange,
    noDataIndication,
    striped,
    hover,
    tableHeaderClass,
    className,
}) => {
    const adapterColumns = useMemo(() => buildAdapterColumns(columns), [columns]);
    // Add an index column at the start that shows a row number based on the
    // external pagination offset (pagination.pageIndex) plus the row position.
    const adapterColumnsWithIndex = useMemo(() => {
        const cols = Array.isArray(adapterColumns) ? [...adapterColumns] : [];
        cols.unshift({ id: '__index', text: '#', headerStyle: { width: '60px' }, isIndex: true });
        return cols;
    }, [adapterColumns]);
    const [expandedKeys, setExpandedKeys] = useState(() => new Set());

    const showExpandColumn = !!expandRow?.showExpandColumn;
    const expandByColumnOnly = !!expandRow?.expandByColumnOnly;

    const toggleExpanded = (rowKey) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }
            return next;
        });
    };

    const handleRowClick = (rowKey) => {
        if (!expandRow || expandByColumnOnly) return;
        toggleExpanded(rowKey);
    };

    const colSpan = adapterColumnsWithIndex.length + (showExpandColumn ? 1 : 0);

    return (
        <div className="table-adapter">
            <table className={classNames('table', className, {
                'table-striped': striped,
                'table-hover': hover,
            })}>
                <thead className={tableHeaderClass}>
                    <tr>
                        {showExpandColumn && <th style={{ width: '40px' }} />}
                        {adapterColumnsWithIndex.map(col => (
                            <th
                                key={col.id}
                                style={col.headerStyle}
                                className={col.headerAlign ? `text-${col.headerAlign}` : undefined}
                            >
                                {col.text}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 && (
                        <tr>
                            <td colSpan={colSpan || 1} className="text-center">
                                {noDataIndication}
                            </td>
                        </tr>
                    )}

                    {data.map((row, rowIndex) => {
                        const rowKey = keyField ? getCellValue(row, keyField) : rowIndex;
                        const isExpanded = expandedKeys.has(rowKey);

                        return (
                            <React.Fragment key={rowKey ?? rowIndex}>
                                <tr onClick={() => handleRowClick(rowKey)}>
                                    {showExpandColumn && (
                                        <td
                                            style={{ cursor: 'pointer', textAlign: 'center' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleExpanded(rowKey);
                                            }}
                                        >
                                            <i className={`fa ${isExpanded ? 'fa-minus' : 'fa-plus'}`} />
                                        </td>
                                    )}
                                    {adapterColumnsWithIndex.map((col) => {
                                        if (col.isIndex) {
                                            const offset = (pagination && typeof pagination.pageIndex === 'number') ? pagination.pageIndex : 0;
                                            const displayIndex = offset + rowIndex + 1;
                                            return (
                                                <td key={col.id} className={col.align ? `text-${col.align}` : undefined}>
                                                    {displayIndex}
                                                </td>
                                            );
                                        }

                                        return (
                                            <td key={col.id} className={col.align ? `text-${col.align}` : undefined}>
                                                {renderCell(col, row)}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {expandRow && isExpanded && (
                                    <tr>
                                        <td colSpan={colSpan || 1}>
                                            {expandRow.renderer(row)}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>

            {remote && pagination && (
                <Paginator
                    pageIndex={pagination.pageIndex}
                    pageSize={pagination.pageSize}
                    totalSize={pagination.totalSize}
                    onTableChange={onTableChange}
                />
            )}
        </div>
    );
};

export default TableAdapter;
